import React, { FC, useState, useEffect, useRef, useMemo } from "react";
import { Modal, Button, message, Tooltip, Watermark } from "antd";
import {
    PlayCircleOutlined,
    PauseCircleOutlined,
    FullscreenOutlined,
    MutedOutlined,
    SoundOutlined
} from "@ant-design/icons";
import "./VideoPlayerModal.scss";

interface VideoPlayerModalProps {
    visible: boolean;
    onClose: () => void;
    videoUrl: string;
    videoName: string;
    videoStatus?: string;
    videoCreateTime?: string;
    videoMessage?: string;
    videoId?: string;
}

export const VideoPlayerModal: FC<VideoPlayerModalProps> = ({
    visible,
    onClose,
    videoUrl,
    videoName,
    videoStatus,
    videoCreateTime,
    videoMessage,
    videoId
}) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [showControls, setShowControls] = useState(true);
    const [isDragging, setIsDragging] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    
    // 新增：用户信息状态
    const [userInfo, setUserInfo] = useState<any>(null);
    
    const videoRef = useRef<HTMLVideoElement>(null);
    const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // 格式化时间显示
    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    // 获取显示的用户名 - 使用useMemo确保稳定性
    const displayName = useMemo(() => {
        return userInfo?.nickname || userInfo?.username || '用户';
    }, [userInfo]);

    // 获取用户信息
    useEffect(() => {
        const storedUserInfo = localStorage.getItem("userInfo");
        if (storedUserInfo) {
            try {
                const user = JSON.parse(storedUserInfo);
                setUserInfo(user);
            } catch (error) {
                console.error('解析用户信息失败:', error);
            }
        }
    }, []);


    // 播放/暂停控制
    const handlePlayPause = () => {
        console.log('handlePlayPause called, isPlaying:', isPlaying);
        console.log('videoRef.current:', videoRef.current);
        console.log('videoUrl:', videoUrl);
        
        if (videoRef.current) {
            if (isPlaying) {
                videoRef.current.pause();
            } else {
                videoRef.current.play().catch(error => {
                    console.error('视频播放失败:', error);
                    message.error('视频播放失败: ' + error.message);
                });
            }
        } else {
            console.error('videoRef.current is null');
        }
    };

    // 静音控制
    const handleMute = () => {
        if (videoRef.current) {
            videoRef.current.muted = !isMuted;
            setIsMuted(!isMuted);
        }
    };



    // 进度条点击处理
    const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (videoRef.current && duration > 0) {
            const progressBar = e.currentTarget;
            const rect = progressBar.getBoundingClientRect();
            const clickX = e.clientX - rect.left;
            const percentage = Math.max(0, Math.min(1, clickX / rect.width));
            const newTime = percentage * duration;
            
            videoRef.current.currentTime = newTime;
            setCurrentTime(newTime);
        }
    };

    // 进度条拖动处理
    const handleProgressMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(true);
        handleProgressClick(e);
    };

    // 控制栏显示/隐藏
    const showControlsTemporarily = () => {
        setShowControls(true);
        if (controlsTimeoutRef.current) {
            clearTimeout(controlsTimeoutRef.current);
        }
        controlsTimeoutRef.current = setTimeout(() => {
            if (isPlaying) {
                setShowControls(false);
            }
        }, 3000);
    };

    // 视频事件处理
    const handleTimeUpdate = () => {
        if (videoRef.current && !isDragging) {
            setCurrentTime(videoRef.current.currentTime);
        }
    };

    const handleLoadedMetadata = () => {
        if (videoRef.current) {
            setDuration(videoRef.current.duration);
        }
    };

    const handlePlay = () => {
        setIsPlaying(true);
        showControlsTemporarily();
    };

    const handlePause = () => {
        setIsPlaying(false);
        setShowControls(true);
    };

    const handleVideoClick = () => {
        if (isPlaying) {
            handlePlayPause();
        } else {
            showControlsTemporarily();
        }
    };

    // 全屏状态监听
    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
        };
    }, []);

    // 全局鼠标事件监听
    useEffect(() => {
        const handleGlobalMouseUp = () => {
            setIsDragging(false);
        };

        const handleGlobalMouseMove = (e: MouseEvent) => {
            if (isDragging && videoRef.current && duration > 0) {
                e.preventDefault();
                const progressBar = document.querySelector('.modal-progress-bar') as HTMLDivElement;
                if (progressBar) {
                    const rect = progressBar.getBoundingClientRect();
                    const clickX = e.clientX - rect.left;
                    const percentage = Math.max(0, Math.min(1, clickX / rect.width));
                    const newTime = percentage * duration;
                    
                    videoRef.current.currentTime = newTime;
                    setCurrentTime(newTime);
                }
            }
        };

        document.addEventListener('mouseup', handleGlobalMouseUp);
        document.addEventListener('mousemove', handleGlobalMouseMove);

        return () => {
            document.removeEventListener('mouseup', handleGlobalMouseUp);
            document.removeEventListener('mousemove', handleGlobalMouseMove);
        };
    }, [isDragging, duration]);

    // 清理定时器
    useEffect(() => {
        return () => {
            if (controlsTimeoutRef.current) {
                clearTimeout(controlsTimeoutRef.current);
            }
        };
    }, []);

    // 设置视频事件监听器
    useEffect(() => {
        const videoElement = videoRef.current;
        if (videoElement) {
            console.log('设置视频事件监听器, videoUrl:', videoUrl);
            console.log('videoElement.src:', videoElement.src);
            
            videoElement.addEventListener('timeupdate', handleTimeUpdate);
            videoElement.addEventListener('loadedmetadata', handleLoadedMetadata);
            videoElement.addEventListener('play', handlePlay);
            videoElement.addEventListener('pause', handlePause);
            videoElement.addEventListener('error', (e) => {
                console.error('视频加载错误:', e);
                message.error('视频加载失败');
            });

            return () => {
                videoElement.removeEventListener('timeupdate', handleTimeUpdate);
                videoElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
                videoElement.removeEventListener('play', handlePlay);
                videoElement.removeEventListener('pause', handlePause);
            };
        }
    }, [visible, videoUrl]);

    // 重置状态当弹窗关闭时
    useEffect(() => {
        if (!visible) {
            setIsPlaying(false);
            setCurrentTime(0);
            setShowControls(true);
            setIsDragging(false);
            if (videoRef.current) {
                videoRef.current.pause();
                videoRef.current.currentTime = 0;
            }
        } else {
            // 当弹窗打开时，重新获取用户信息
            const storedUserInfo = localStorage.getItem("userInfo");
            if (storedUserInfo) {
                try {
                    const user = JSON.parse(storedUserInfo);
                    setUserInfo(user);
                } catch (error) {
                    console.error('解析用户信息失败:', error);
                }
            }
        }
    }, [visible]);

    return (
        <Modal
            open={visible}
            onCancel={onClose}
            footer={null}
            width="90vw"
            style={{ maxWidth: '1200px' }}
            className="video-player-modal"
            destroyOnClose
        >
            <div className="video-modal-container">
                {/* 视频播放区域 */}
                <div className="video-player-wrapper">
                    {/* 视频标题 - 显示在左上角 */}
                    <div className="video-title-overlay">
                        <h3 className="video-title">{videoName}</h3>
                    </div>
                    
                    {/* 水印包裹视频 */}
                    <Watermark 
                        key={`watermark-${displayName}-${visible}`}
                        content={displayName} 
                        className="video-watermark"
                        font={{ color: 'rgba(255, 255, 255, 0.27)', fontSize: 25 }}
                        gap={[100, 100]}
                        offset={[50, 50]}
                    >
                        <video
                            ref={videoRef}
                            src={videoUrl}
                            className="modal-video-player"
                            onClick={handleVideoClick}
                            onTimeUpdate={handleTimeUpdate}
                            onLoadedMetadata={handleLoadedMetadata}
                            onPlay={handlePlay}
                            onPause={handlePause}
                            onMouseMove={showControlsTemporarily}
                            onMouseLeave={() => {
                                if (isPlaying) {
                                    setShowControls(false);
                                }
                            }}
                            onError={(e) => {
                                console.error('视频加载错误:', e);
                                message.error('视频加载失败，请检查视频链接');
                            }}
                            onLoadStart={() => {
                                console.log('视频开始加载');
                            }}
                            onCanPlay={() => {
                                console.log('视频可以播放');
                            }}
                            preload="metadata"
                            controls={false}
                        />
                    </Watermark>

                    {/* 自定义控制栏 */}
                    <div className={`modal-custom-controls ${showControls ? 'show' : 'hide'}`}>
                        {/* 进度条 */}
                        <div 
                            className="modal-progress-bar"
                            onClick={handleProgressClick}
                            onMouseDown={handleProgressMouseDown}
                        >
                            <div 
                                className="modal-progress-fill"
                                style={{ width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%` }}
                            ></div>
                        </div>

                        <div className="modal-controls-main">
                            <div className="modal-controls-left">
                                <Button
                                    type="text"
                                    icon={isPlaying ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                                    onClick={handlePlayPause}
                                    className="modal-play-btn"
                                />
                                <span className="modal-time-display">
                                    {formatTime(currentTime)} / {formatTime(duration)}
                                </span>
                            </div>

                            <div className="modal-controls-right">
                                <Tooltip title={isMuted ? "取消静音" : "静音"}>
                                    <Button
                                        type="text"
                                        icon={isMuted ? <MutedOutlined /> : <SoundOutlined />}
                                        onClick={handleMute}
                                        className="modal-mute-btn"
                                    />
                                </Tooltip>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    );
};
